/** StageGimmickIcons — ステージ JSON からプレビュー用ギミックアイコンを推定する。
 *  Reference: ステージマップ UI、ホーム画面で各ステージの個性を可視化。
 *  Callers: HomeScreen.ts の setStages() でステージノードのアイコン生成に使用。
 */

import type { StageJson } from './StageLoader';

export interface GimmickIcon {
  readonly id: string;
  readonly icon: string;
  readonly label: string;
  /** 優先度(高いものを先に表示) */
  readonly priority: number;
}

/** ステージ JSON から代表的なギミック上位 N 個を抽出 */
export function extractGimmickIcons(stage: StageJson, maxIcons = 3): GimmickIcon[] {
  const set = new Map<string, GimmickIcon>();
  const add = (g: GimmickIcon): void => {
    if (!set.has(g.id)) set.set(g.id, g);
  };

  // ボスステージは最優先
  if (stage.stage_type === 'T5_boss') {
    add({ id: 'boss', icon: '👑', label: 'ボス', priority: 100 });
  }

  // Enemy AI
  for (const e of stage.map.enemies) {
    switch (e.ai) {
      case 'player_track':
        add({ id: 'chaser', icon: '🔴', label: '追跡敵', priority: 90 });
        break;
      case 'trail_target':
        add({ id: 'stalker', icon: '🟣', label: 'トレイル狩り', priority: 85 });
        break;
      case 'sleeper':
        add({ id: 'sleeper', icon: '💤', label: '眠る影', priority: 80 });
        break;
      case 'patroller':
        add({ id: 'patroller', icon: '🔵', label: '巡回騎士', priority: 70 });
        break;
      case 'guard':
        add({ id: 'guard', icon: '🛡', label: '門番', priority: 72 });
        break;
      case 'mirror_echo':
        add({ id: 'mirror_echo', icon: '🫥', label: '残像', priority: 88 });
        break;
      case 'random_walk':
        // 普通の敵はアイコン不要
        break;
    }
  }

  // G1: Teleporters
  if (stage.map.teleporters && stage.map.teleporters.length > 0) {
    add({ id: 'teleporter', icon: '🌀', label: 'ワープ', priority: 78 });
  }

  // G2/G3: obstacle types
  for (const ob of stage.map.obstacles) {
    if (ob.type === 'color_gate') {
      add({ id: 'color_gate', icon: '🚪', label: '色扉', priority: 73 });
    } else if (ob.type === 'sliding_wall') {
      add({ id: 'sliding_wall', icon: '🚧', label: '動く壁', priority: 74 });
    }
  }

  // Special Zones
  for (const z of stage.map.special_zones) {
    switch (z.type) {
      case 'wind_stream':
        add({ id: 'wind', icon: '💨', label: '風筋', priority: 75 });
        break;
      case 'honey_trap':
        add({ id: 'honey', icon: '🍯', label: '蜂蜜だまり', priority: 65 });
        break;
      case 'mirror_canvas':
        add({ id: 'mirror', icon: '🪞', label: '鏡映', priority: 75 });
        break;
      case 'color_pool':
        add({ id: 'pool', icon: '🎨', label: '太筆', priority: 60 });
        break;
      case 'wet_paint':
        add({ id: 'wet', icon: '💧', label: '濡れ顔料', priority: 50 });
        break;
      case 'speed_boost':
        add({ id: 'speed', icon: '⚡', label: '加速帯', priority: 40 });
        break;
      case 'slow_zone':
        add({ id: 'slow', icon: '🌀', label: '沼', priority: 40 });
        break;
    }
  }

  // Collectibles(特別なものだけ)
  for (const c of stage.map.collectibles) {
    switch (c.type) {
      case 'time_crystal':
        add({ id: 'time', icon: '⏱', label: '時の砂', priority: 55 });
        break;
      case 'shield_bubble':
        add({ id: 'shield', icon: '🫧', label: '虹のシールド', priority: 50 });
        break;
      case 'coverage_token':
        add({ id: 'token', icon: '💎', label: '塗りトークン', priority: 55 });
        break;
      case 'quick_dash':
        add({ id: 'dash', icon: '🏃', label: '疾走', priority: 50 });
        break;
      case 'art_note':
        add({ id: 'note', icon: '📜', label: 'アートノート', priority: 30 });
        break;
      case 'hourglass':
        add({ id: 'hourglass', icon: '⏳', label: 'タイマー+30秒', priority: 57 });
        break;
      case 'magnet_pigment':
        add({ id: 'magnet', icon: '🧲', label: '磁石顔料', priority: 53 });
        break;
      case 'painting_fragment':
        add({ id: 'fragment', icon: '🧩', label: '名画ピース', priority: 48 });
        break;
      // color_bonus / heart_bonus は省略
      default:
        break;
    }
  }

  // 壁が 4 個以上なら迷路アイコン
  if (stage.map.obstacles.length >= 4) {
    add({ id: 'maze', icon: '🧱', label: '迷路', priority: 45 });
  }

  return Array.from(set.values())
    .sort((a, b) => b.priority - a.priority)
    .slice(0, maxIcons);
}
